import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { pushSupported, subscribeToPush, unsubscribeFromPush, isPushSubscribed, showLocalTestNotification } from "@/lib/push";

// Shown when the phone's OS has hard-blocked notifications/location for the app.
const DEVICE_SETTINGS_MSG =
  "Cheapify needs permission from your phone. Please go to your device Settings → Apps → Cheapify to turn on Location and Notifications manually.";

// Settings row to enable/disable real Web Push notifications on this device.
export default function NotificationToggle() {
  const [supported] = useState(pushSupported());
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (supported) isPushSubscribed().then(setEnabled).catch(() => {});
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      // If the phone's OS/browser has HARD-BLOCKED notifications, requestPermission()
      // silently returns "denied" without a prompt — so guide the user to device settings.
      if (Notification.permission === "denied") {
        toast.error(DEVICE_SETTINGS_MSG, { duration: 10000 });
        setBusy(false);
        return;
      }
      if (Notification.permission !== "granted") {
        const p = await Notification.requestPermission();
        if (p === "denied") {
          toast.error(DEVICE_SETTINGS_MSG, { duration: 10000 });
          setBusy(false);
          return;
        }
        if (p !== "granted") {
          toast.error("You dismissed the prompt. Tap Allow again and accept it to turn notifications on.");
          setBusy(false);
          return;
        }
      }
      const r = await subscribeToPush();
      if (r.ok) {
        setEnabled(true);
        toast.success("Notifications on — we'll alert you about new features & deals.");
      } else {
        toast.error("Couldn't enable notifications on this device.");
      }
    } catch (e) {
      console.error("enable push failed:", e);
      toast.error("Couldn't enable notifications.");
    }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setEnabled(false);
      toast.success("Notifications turned off.");
    } catch (e) {
      console.error("disable push failed:", e);
    }
    setBusy(false);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await showLocalTestNotification();
      if (r.ok) toast.success("Test notification fired! Check your notifications (minimise the app on your phone).");
      else if (r.reason === "denied") toast.error(DEVICE_SETTINGS_MSG, { duration: 10000 });
      else toast.error("Couldn't show the test notification on this device.");
    } catch (e) {
      console.error("test push failed:", e);
      toast.error("Couldn't show the test notification.");
    }
    setTesting(false);
  };

  if (!supported) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm mt-4 flex items-center gap-3 p-5" data-testid="notification-toggle">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900">
          <BellOff className="h-5 w-5 text-brand-yellow" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold">🔔 Push Notifications</p>
          <p className="text-xs text-gray-500">Not available on this browser. Try the installed app or a supported browser to get deal & update alerts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm mt-4 p-5" data-testid="notification-toggle">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900">
          {enabled ? <Bell className="h-5 w-5 text-brand-yellow" /> : <BellOff className="h-5 w-5 text-brand-yellow" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold">🔔 Push Notifications</p>
          <p className="text-xs text-gray-500">
            {enabled ? "On — you'll get new-feature & deal alerts." : "Off — tap Allow and your browser will ask permission."}
          </p>
        </div>
        <button
          type="button"
          onClick={enabled ? disable : enable}
          disabled={busy}
          data-testid="notification-toggle-btn"
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-xs font-bold uppercase tracking-[0.06em] transition-transform hover:scale-105 disabled:opacity-60 ${
            enabled ? "border border-gray-300 bg-white text-gray-700" : "bg-gradient-to-r from-brand-yellow to-brand-orange text-black"
          }`}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {enabled ? "Turn Off" : "Allow"}
        </button>
      </div>

      {enabled && (
        <button
          type="button"
          onClick={sendTest}
          disabled={testing}
          data-testid="notification-test-btn"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 py-2.5 text-xs font-bold uppercase tracking-[0.06em] text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-60"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send a test notification
        </button>
      )}
    </div>
  );
}
